import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import i18n from '../i18n';
import { ApiError } from '../services/api';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Run the loader again (after a write). */
  reload: () => void;
}

/** Load `fn` when `deps` change; a stale answer (deps changed meanwhile) is dropped. */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    fn().then(
      (result) => {
        if (mine !== seq.current) return;
        setData(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (mine !== seq.current) return;
        setError(errorMessage(err));
        setLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, error, loading, reload };
}

export function errorMessage(err: unknown): string {
  // A refusal code the API names (`vat_review_pending`, `invalid_siren`…) reads as its sentence when one exists.
  if (err instanceof ApiError) return (err.detail && i18n.exists(`errors.${err.detail}`) ? i18n.t(`errors.${err.detail}`) : err.detail) || `HTTP ${err.status}`;
  if (err instanceof Error) return err.message;
  return String(err);
}
